import { useQuery } from '@tanstack/react-query';

import type { GetCompleteMSOResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to get complete MSO status from a purchased vehicle
 */
export const getCompleteMSO = async (
	servicesProviderId: string,
	orderId: string,
): Promise<GetCompleteMSOResponse> => {
	const response = await apiClient.get<GetCompleteMSOResponse>(
		ordersEndpoints.completeMSO(servicesProviderId, orderId),
	);
	return response.data;
};

/**
 * Hook for getting complete MSO status from a purchased vehicle
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useGetCompleteMSO = (orderId: string | undefined) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.orders.detail(servicesProviderId, orderId || '')._ctx.progress._ctx
			.completeMSO.queryKey,
		queryFn: () => {
			if (!orderId) {
				throw new Error('Unable to get complete MSO status without an order ID.');
			}
			return getCompleteMSO(servicesProviderId, orderId);
		},
		enabled: Boolean(orderId) && Boolean(servicesProviderId),
		staleTime: Infinity,
		...errorBoundaryOptions,
	});
};
